import { useEffect, useState } from "react"
import BaseModal from "../../../../components/layout/BaseModal"
import { Employee } from "../../../Mitarbeiter/types/Types"
import dayjs, { Dayjs } from "dayjs"
import weekOfYear from "dayjs/plugin/weekOfYear"
import { useOptimizeRouteMutation } from "../../api/services"
import { useQueryClient } from "@tanstack/react-query"
import { ConfigDayType, OptimizeRouteResultType } from "../../types"
import RouteOptimizationModalScreen1 from "./RouteOptimizationModalScreen1"
import RouteOptimizationModalScreen2 from "./RouteOptimizationModalScreen2"
import { loadWithExpiryFromLocalStorage } from "../../../../services/functions"

dayjs.extend(weekOfYear)

type RouteOptimizationModalProps = {
    modalId: number
    employee: Employee
    startDate: Dayjs
    queryKey: string
    closeModal: () => void
    setOptimizedResult: (result: OptimizeRouteResultType | null) => void
}

export default function RouteOptimizationModal(props: RouteOptimizationModalProps) {
    const [selectedDays, setSelectedDays] = useState<{
        monday: ConfigDayType
        tuesday: ConfigDayType
        wednesday: ConfigDayType
        thursday: ConfigDayType
        friday: ConfigDayType
    }>({
        monday: {
            enabled: true,
            label: "Montag",
        },
        tuesday: {
            enabled: true,
            label: "Dienstag",
        },
        wednesday: {
            enabled: true,
            label: "Mittwoch",
        },
        thursday: {
            enabled: true,
            label: "Donnerstag",
        },
        friday: {
            enabled: true,
            label: "Freitag",
        },
    })

    const [globalStartTime, setGlobalStartTime] = useState<string>("08:00")
    const [globalEndTime, setGlobalEndTime] = useState<string>("17:00")

    const handleChangeDayConfig = (day: keyof typeof selectedDays, attribute: keyof ConfigDayType, value: any) => {
        setSelectedDays((prev) => ({ ...prev, [day]: { ...prev[day], [attribute]: value } }))
    }

    // optimize route
    const queryClient = useQueryClient()
    const { mutate: optimizeRoute, isSuccess: optimizeRouteSuccess, data: result } = useOptimizeRouteMutation(queryClient, props.queryKey)
    const handleOptimizeRoute = () => {
        optimizeRoute({
            id: props.employee._id ?? "",
            body: {
                weekNumber: dayjs(props.startDate).week(),
                year: dayjs(props.startDate).year(),
                optimizeSessionDurationByTaskEstimation: false,
                weekDayConfig: {
                    globalStartTime: globalStartTime,
                    globalEndTime: globalEndTime,
                    days: selectedDays,
                },
            },
        })
    }

    // check if result is in session storage / set result in session storage if success

    const [resultPage, setResultPage] = useState<OptimizeRouteResultType | null>(null)
    useEffect(() => {
        if (localStorage.getItem("resultOfOptimization")) {
            setResultPage(loadWithExpiryFromLocalStorage("resultOfOptimization"))
        }
    }, [])
    useEffect(() => {
        if (optimizeRouteSuccess && (!resultPage || Object.keys(resultPage as OptimizeRouteResultType).length === 0)) {
            const resultOfOptimization = (result as any).optimizationResult as OptimizeRouteResultType
            localStorage.setItem("resultOfOptimization", JSON.stringify({
                value: resultOfOptimization,
                expiry: new Date().getTime() + 1000 * 60 * 60, // 1 hour in milliseconds
            }))
            props.setOptimizedResult(resultOfOptimization)
            setResultPage(resultOfOptimization)
        }
    }, [optimizeRouteSuccess, result, resultPage])



    // close modal and remove result from session storage
    const doneWithOptimization = () => {
        props.setOptimizedResult(null)
        setResultPage(null)
        localStorage.removeItem("resultOfOptimization")
        props.closeModal()
    }

    return (
        <BaseModal
            title="Routenoptimierung"
            modalId={props.modalId}
            bottomRow={
                <div className="flex w-full justify-end">
                    {resultPage && Object.keys(resultPage).length !== 0 ? (
                        <button className="btn btn-primary" onClick={doneWithOptimization}>
                            Abschließen
                        </button>
                    ) : (
                        <button className="btn btn-primary" onClick={handleOptimizeRoute}>
                            Route Optimieren
                        </button>
                    )}
                </div>
            }>
            <div className="overflow-hidden relative h-full w-full">
                <RouteOptimizationModalScreen1
                    startDate={dayjs(props.startDate).toDate()}
                    visible={!resultPage || Object.keys(resultPage).length === 0}
                    globalStartTime={globalStartTime}
                    setGlobalStartTime={setGlobalStartTime}
                    globalEndTime={globalEndTime}
                    setGlobalEndTime={setGlobalEndTime}
                    selectedDays={selectedDays}
                    handleChangeDayConfig={handleChangeDayConfig}
                    employee={props.employee}
                />
                <RouteOptimizationModalScreen2 visible={(resultPage && Object.keys(resultPage).length !== 0) ?? false} resultPage={resultPage ?? undefined} />
            </div>
        </BaseModal>
    )
}
